// Browser-only preview with an idealized controller, explicitly enabled by
// ?mock=1. It exercises the real transport/UI/renderer, but is not a substitute
// for firmware or hardware acceptance (notably unsafe student-output tests).
function encode(state) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(state)) {
    params.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  return params.toString();
}

export function createMockBridge(deliver, timers = window) {
  let seq = 0;
  let lastRequestId = 0;
  let transitionTimer = null;
  let heartbeatTimer = null;
  let yawTransition = null;
  let destroyed = false;
  const deliveryTimers = new Set();
  const state = {
    v: 1,
    seq: 0,
    scenario: 'calm',
    phase: 'reset',
    windDirection: 0,
    windBand: 'calm',
    aligned: false,
    nacelleAngle: 0,
    targetAngle: 0,
    rotorBand: 'stopped',
    align: false,
    generatorEnable: false,
    active: false,
    runwayWindAlert: false,
    activeRequestId: 0,
    notice: 'none',
  };

  function report(delay = 20) {
    if (destroyed) return;
    updateYaw();
    // Capture now so a later timer cannot accidentally deliver mutated state.
    const snapshot = { ...state, seq: ++seq };
    const timer = timers.setTimeout(() => {
      deliveryTimers.delete(timer);
      deliver(encode(snapshot));
    }, delay);
    deliveryTimers.add(timer);
  }

  function updateYaw() {
    if (!yawTransition) return;
    const { start, travel, startedAt } = yawTransition;
    const fraction = Math.min(1, (Date.now() - startedAt) / 2500);
    state.nacelleAngle = (Math.round(start + travel * fraction) + 360) % 360;
  }

  function clearTransition() {
    updateYaw();
    yawTransition = null;
    if (transitionTimer !== null) timers.clearTimeout(transitionTimer);
    transitionTimer = null;
  }

  function calm() {
    const moving = state.rotorBand !== 'stopped';
    Object.assign(state, {
      scenario: 'calm', phase: 'calm', windDirection: 0, windBand: 'calm', aligned: false,
      targetAngle: state.nacelleAngle, rotorBand: moving ? 'decelerating' : 'stopped', align: false,
      generatorEnable: false, active: moving, runwayWindAlert: false, notice: 'none',
    });
    report();
    if (moving) {
      transitionTimer = timers.setTimeout(() => {
        Object.assign(state, { rotorBand: 'stopped', active: false });
        report();
      }, 1000);
    }
  }

  function startSteady(scenario) {
    const targetAngle = (state.targetAngle + 90) % 360;
    // Heartbeats publish intermediate yaw, so the nacelle turns while aligning
    // instead of jumping to the bearing only after generation starts.
    yawTransition = {
      start: state.nacelleAngle,
      travel: ((targetAngle - state.nacelleAngle + 540) % 360) - 180,
      startedAt: Date.now(),
    };
    Object.assign(state, {
      scenario,
      phase: scenario === 'realign' ? 'realigning' : 'aligning',
      windDirection: targetAngle,
      windBand: 'steady',
      aligned: false,
      targetAngle,
      rotorBand: state.rotorBand === 'running' ? 'decelerating' : 'stopped',
      align: true,
      generatorEnable: false,
      active: true,
      runwayWindAlert: false,
      notice: 'none',
    });
    report();
    transitionTimer = timers.setTimeout(() => {
      Object.assign(state, {
        phase: 'generating', aligned: true, nacelleAngle: targetAngle,
        rotorBand: 'running', align: false, generatorEnable: true,
      });
      report();
    }, 2500);
  }

  function highWind() {
    Object.assign(state, {
      scenario: 'highWind', phase: 'highWind', windBand: 'high', aligned: false,
      rotorBand: state.rotorBand === 'stopped' ? 'stopped' : 'decelerating',
      align: false, generatorEnable: false, active: true, runwayWindAlert: true, notice: 'none',
    });
    report();
    transitionTimer = timers.setTimeout(() => {
      Object.assign(state, { rotorBand: 'stopped', active: false });
      report();
    }, 1000);
  }

  function restart() {
    Object.assign(state, {
      scenario: 'calm', phase: 'reset', windDirection: 0, windBand: 'calm', aligned: false,
      nacelleAngle: 0, targetAngle: 0, rotorBand: 'stopped', notice: 'none',
    });
    report();
    transitionTimer = timers.setTimeout(calm, 250);
  }

  function send(raw) {
    if (destroyed) return;
    const params = new URLSearchParams(raw);
    const requestId = Number(params.get('requestId'));
    if (requestId === lastRequestId) {
      state.activeRequestId = requestId;
      report();
      return;
    }
    lastRequestId = requestId;
    state.activeRequestId = requestId;

    const action = params.get('action');
    if (action === 'sync') {
      // Sync is observational: do not add another startup/calm timer. Otherwise
      // a quick scenario choice can be overwritten by an orphaned startup timer.
      report();
    }
    else {
      clearTransition();
      if (action === 'restart') restart();
      else if (action === 'scenario' && params.get('name') === 'calm') calm();
      else if (action === 'scenario' && params.get('name') === 'steady') startSteady('steady');
      else if (action === 'scenario' && params.get('name') === 'realign') startSteady('realign');
      else if (action === 'scenario' && params.get('name') === 'highWind') highWind();
    }
  }

  transitionTimer = timers.setTimeout(calm, 250);
  heartbeatTimer = timers.setInterval(() => report(0), 450);
  return {
    send,
    destroy() {
      destroyed = true;
      clearTransition();
      if (heartbeatTimer !== null) timers.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
      deliveryTimers.forEach((timer) => timers.clearTimeout(timer));
      deliveryTimers.clear();
    },
  };
}
